import set from 'lodash/set'
import { z } from 'zod'
import { userProperties } from './BaseModels'

export const userPermissions = {
  bookings: z.boolean(),
  view_private: z.boolean(),
  edit_profile: z.boolean(),
  edit: z.boolean(),
  delete: z.boolean(),
  change_status: z.boolean(),
  reset_password: z.boolean(),
  assign_learning_item: z.boolean(),
  edit_learning_assignment: z.boolean(),
  delete_learning_assignment: z.boolean(),
  mark_learning_item_as_finished: z.boolean(),
}

export const groupPermissions = {
  view_private: z.boolean(),
  edit: z.boolean(),
  add_manager: z.boolean(),
  remove_manager: z.boolean(),
  add_member: z.boolean(),
  remove_member: z.boolean(),
  assign_learning_item: z.boolean(),
  view_learning_assignment: z.boolean(),
  edit_learning_assignment: z.boolean(),
  delete_learning_assignment: z.boolean(),
  comment: z.boolean(),
  comment_delete_permanent: z.boolean(),
}

export const permissionsParser = z.object({
  switch_client: z.boolean(),
  category_create: z.boolean(),
  provider_create: z.boolean(),
  provider_create_global: z.boolean(),
  trainer_create: z.boolean(),
  trainer_create_global: z.boolean(),
  learning_item_create: z.boolean(),
  learning_path_create: z.boolean(),
  scheduled_course_create: z.boolean(),
  scheduled_course_create_global: z.boolean(),
  on_demand_course_create: z.boolean(),
  on_demand_course_create_global: z.boolean(),
  learning_item: z.object({
    book: z.boolean(),
    view_draft: z.boolean(),
    view_draft_global: z.boolean(),
    view_deprecated: z.boolean(),
    preview: z.boolean(),
  }),
  group_create: z.boolean(),
  group: z.object({
    ...groupPermissions,
  }),
  user_create_user: z.boolean(),
  user_create_advanced: z.boolean(),
  user_create_admin: z.boolean(),
  user_create_auditor: z.boolean(),
  user_create_csm: z.boolean(),
  user: z.object({
    ...userPermissions,
  }),
})

export type Permissions = z.infer<typeof permissionsParser>

export const managedGroupsParser = z.array(
  z.object({
    uuid: z.string(),
  })
)

export type ManagedGroup = z.infer<typeof managedGroupsParser>

function generateDefaultPermissions(parser: any): any {
  if (parser.shape) {
    return Object.fromEntries(Object.entries(parser.shape).map(([k, v]) => [k, generateDefaultPermissions(v)]))
  }
  return false
}

const defaultPermissions = generateDefaultPermissions(permissionsParser)

export const currentUserParser = z.preprocess(
  (o: any) => {
    const permissions = structuredClone(defaultPermissions)
    for (const p of o.permissions || []) {
      set(permissions, p, true)
    }
    o.permissions = permissions
    return o
  },
  z.object({
    ...userProperties,
    permissions: permissionsParser,
    assumableClients: z
      .array(
        z.object({
          uuid: z.string(),
          name: z.string(),
        })
      )
      .nullish(),
    client: z.object({
      uuid: z.string(),
      name: z.string(),
      code: z.string(),
    }),
    passwordResetRequired: z.boolean().nullish(),
    branding: z.string().nullish(),
    managedGroups: managedGroupsParser,
  })
)

export type CurrentUser = z.infer<typeof currentUserParser>
